import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Sprout, 
  Calendar as CalendarIcon, 
  List, 
  BarChart3, 
  Plus, 
  Share2, 
  Settings, 
  Lock, 
  Globe, 
  Check, 
  AlertCircle,
  SunMedium,
  Award,
  Loader2
} from 'lucide-react';
import { Job, Entry, User, ProjectData } from '../types';
import { api } from '../api/client';
import { TimelineView } from '../components/TimelineView';
import { CalendarView } from '../components/CalendarView';
import { AnalyticsView } from '../components/AnalyticsView';
import { NewEntryModal } from '../components/NewEntryModal';
import { ProjectSettingsModal } from '../components/ProjectSettingsModal';

interface ProjectPageProps {
  user: User | null;
  onOpenAuth: () => void;
}

export const ProjectPage: React.FC<ProjectPageProps> = ({ user, onOpenAuth }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active view tab: 'timeline' | 'calendar' | 'analytics'
  const [activeTab, setActiveTab] = useState<'timeline' | 'calendar' | 'analytics'>('timeline');

  // Modals
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | undefined>(undefined);
  const [editingEntry, setEditingEntry] = useState<Entry | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  // Toast feedback
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const loadProject = async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getProject(slug);
      setProjectData(data);
    } catch (err: any) {
      setError(err.message || 'Error loading project');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
  }, [slug]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      showToast('URL copied to clipboard! Share it with anyone.');
    } else {
      showToast('Link: ' + url);
    }
  };

  const handleSaveEntry = (newEntry: Entry) => {
    if (!projectData) return;
    const exists = projectData.entries.some((e) => e.id === newEntry.id);
    let updatedEntries: Entry[];
    if (exists) {
      updatedEntries = projectData.entries.map((e) => (e.id === newEntry.id ? newEntry : e));
    } else {
      updatedEntries = [newEntry, ...projectData.entries];
    }
    setProjectData({
      ...projectData,
      entries: updatedEntries,
    });
    showToast('Entry successfully recorded!');
  };

  const handleDeleteEntry = async (id: string) => {
    if (!projectData) return;
    await api.deleteEntry(id);
    setProjectData({
      ...projectData,
      entries: projectData.entries.filter((e) => e.id !== id),
    });
    showToast('Entry deleted.');
  };

  const handleUpdateJob = (updatedJob: Job) => {
    if (!projectData) return;
    setProjectData({
      ...projectData,
      job: updatedJob,
    });
    showToast('Project settings updated.');
  };

  if (loading) {
    return (
      <div className="main-container" style={{ textAlign: 'center', padding: '6rem 0' }}>
        <Loader2 size={36} className="animate-spin" color="var(--accent-emerald)" style={{ margin: '0 auto 1rem' }} />
        <h3 style={{ fontSize: '1.2rem', color: 'var(--text-muted)' }}>Loading live project timeline...</h3>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-container" style={{ maxWidth: 600, textAlign: 'center', padding: '5rem 1rem' }}>
        <div className="glass-panel" style={{ padding: '2.5rem' }}>
          <AlertCircle size={42} color="var(--accent-rose)" style={{ margin: '0 auto 1rem' }} />
          <h2 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>Access Restricted</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.75rem', lineHeight: 1.6 }}>{error}</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/')}>
              Return Home
            </button>
            <button className="btn btn-primary" onClick={onOpenAuth}>
              Sign In to Authenticate
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!projectData) return null;

  const { job, entries } = projectData;

  return (
    <div className="main-container">
      {/* Top Banner */}
      <div className="glass-panel project-banner">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <span className="badge badge-emerald" style={{ textTransform: 'capitalize' }}>
                <Sprout size={12} />
                <span>{job.category}</span>
              </span>

              {job.is_private ? (
                <span className="badge badge-purple">
                  <Lock size={12} />
                  <span>Private Lock (Pro)</span>
                </span>
              ) : (
                <span className="badge badge-cyan">
                  <Globe size={12} />
                  <span>Public Obfuscated Link</span>
                </span>
              )}

              <span style={{ fontSize: '0.78rem', color: 'var(--text-sub)' }}>
                /{job.slug}
              </span>
            </div>

            <h1 style={{ fontSize: '2.2rem', color: 'var(--text-main)', marginBottom: '0.35rem' }}>
              {job.title}
            </h1>
            {job.description && (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', maxWidth: 650 }}>
                {job.description}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" onClick={handleShare}>
              <Share2 size={14} />
              <span>Share URL</span>
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowSettings(true)}>
              <Settings size={14} />
              <span>Settings</span>
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setEditingEntry(null);
                setSelectedCalendarDate(undefined);
                setShowNewEntry(true);
              }}
            >
              <Plus size={16} />
              <span>Record Entry</span>
            </button>
          </div>
        </div>

        {/* Quick Highlights Strip */}
        <div className="project-stats-grid">
          <div className="stat-card">
            <div className="stat-label">Plant Variety</div>
            <div className="stat-value" style={{ fontSize: '1.25rem' }}>
              {job.settings.plantVariety || 'Not specified'}
            </div>
            <div className="stat-sub">{job.settings.currentStage || 'Vegetative'}</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Light Schedule</div>
            <div className="stat-value" style={{ fontSize: '1.25rem', color: '#f59e0b' }}>
              {job.settings.lightSchedule || '18/6'}
            </div>
            <div className="stat-sub">Cycle</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Total Logs</div>
            <div className="stat-value" style={{ fontSize: '1.25rem' }}>
              {entries.length}
            </div>
            <div className="stat-sub">Calendar entries</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Target Yield</div>
            <div className="stat-value" style={{ fontSize: '1.25rem', color: 'var(--accent-emerald-light)' }}>
              {job.settings.targetYield || 500} {job.settings.yieldUnit || 'g'}
            </div>
            <div className="stat-sub">Goal</div>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="view-tabs">
        <button
          className={`tab-btn ${activeTab === 'timeline' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          <List size={16} />
          <span>Timeline Feed ({entries.length})</span>
        </button>

        <button
          className={`tab-btn ${activeTab === 'calendar' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          <CalendarIcon size={16} />
          <span>Calendar Schedule</span>
        </button>

        <button
          className={`tab-btn ${activeTab === 'analytics' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <BarChart3 size={16} />
          <span>Growth & Yield Analytics</span>
        </button>
      </div>

      {/* Main Content Area */}
      {activeTab === 'timeline' && (
        <TimelineView
          entries={entries}
          customFields={job.settings.customFields}
          onDeleteEntry={handleDeleteEntry}
          onEditEntry={(entry) => {
            setEditingEntry(entry);
            setShowNewEntry(true);
          }}
        />
      )}

      {activeTab === 'calendar' && (
        <CalendarView
          entries={entries}
          onSelectDate={(date) => {
            setSelectedCalendarDate(date);
          }}
          onAddEntryForDate={(date) => {
            setEditingEntry(null);
            setSelectedCalendarDate(date);
            setShowNewEntry(true);
          }}
          onSelectEntry={(entry) => {
            setEditingEntry(entry);
            setShowNewEntry(true);
          }}
        />
      )}

      {activeTab === 'analytics' && (
        <AnalyticsView job={job} entries={entries} />
      )}

      {/* Floating Action Button (Mobile PWA) */}
      <button
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          color: '#fff',
          border: 'none',
          boxShadow: '0 8px 24px rgba(16, 185, 129, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 40,
        }}
        onClick={() => {
          setEditingEntry(null);
          setSelectedCalendarDate(undefined);
          setShowNewEntry(true);
        }}
        title="Record New Entry"
      >
        <Plus size={26} />
      </button>

      {/* Modals */}
      {showNewEntry && (
        <NewEntryModal
          job={job}
          initialDate={selectedCalendarDate}
          editingEntry={editingEntry}
          onClose={() => {
            setShowNewEntry(false);
            setEditingEntry(null);
          }}
          onSave={handleSaveEntry}
        />
      )}

      {showSettings && (
        <ProjectSettingsModal
          job={job}
          user={user}
          onClose={() => setShowSettings(false)}
          onSave={handleUpdateJob}
          onOpenAuth={onOpenAuth}
        />
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="toast-notice">
          <Check size={16} color="var(--accent-emerald)" />
          <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
